/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 29, 2026 by hoarfroster
 *
 */

import { getStorageItem, setStorageItem } from '@utils/storage';
import {
    ActivityModuleKeys,
    AdminModuleKeys,
    AppModuleKeys,
    AuthModuleKeys,
    ContactModuleKeys,
    CookingModuleKeys,
    CredentialModuleKeys,
    FileModuleKeys,
    I18NModuleDict,
    MomentModuleKeys,
    NoteModuleKeys,
    PostModuleKeys,
    RemarkModuleKeys,
    SettingsModuleKeys,
} from '@utils/i18n.extended';

export type Locale = 'en-US' | 'zh-CN';
export const I18NLocales = ['en-US', 'zh-CN'],
    I18NLocaleNames = ['English', '简体中文'];

/** Locale aliases so that e.g. zh-CHS stored in the module dict under
 *  zh-CN can also be resolved from the user's browser locale. */
const LOCALE_FALLBACK: Record<string, string> = {
    'zh-TW': 'zh-CN', // fallback chain: zh-TW → zh-CN if zh-TW module missing
};

function normalizeLocale(locale?: string | null): Locale | null {
    if (!locale) return null;
    if (I18NLocales.includes(locale)) return locale as Locale;

    const normalized = locale.replace('_', '-');
    const lower = normalized.toLowerCase();
    if (lower.startsWith('en')) return 'en-US';
    if (lower === 'zh-tw' || lower === 'zh-hk' || lower.includes('hant')) return 'zh-CN';
    if (lower.startsWith('zh')) return 'zh-CN';

    return null;
}

let currentLocale: Locale = (() => {
    const stored = getStorageItem('locale');
    const resolvedStored = normalizeLocale(stored);
    if (resolvedStored) return resolvedStored;

    const browserLocale =
        typeof navigator !== 'undefined' ? normalizeLocale(navigator.languages?.[0] ?? navigator.language) : null;
    const resolved = browserLocale ?? 'zh-CN';
    setStorageItem('locale', resolved);
    return resolved;
})();

function setLocale(locale: Locale) {
    currentLocale = locale;
    if (typeof localStorage !== 'undefined') {
        localStorage.setItem('locale', locale);
    }
}

// ── module sub-proxy ───────────────────────────────────────────────

interface ModuleKeysMap {
    note: NoteModuleKeys;
    contact: ContactModuleKeys;
    activity: ActivityModuleKeys;
    file: FileModuleKeys;
    moment: MomentModuleKeys;
    cooking: CookingModuleKeys;
    admin: AdminModuleKeys;
    app: AppModuleKeys;
    auth: AuthModuleKeys;
    settings: SettingsModuleKeys;
    credential: CredentialModuleKeys;
    post: PostModuleKeys;
    remark: RemarkModuleKeys;
}

type ModuleProxy<K extends string> = {
    readonly [P in K]: string;
} & {
    /** Resolve a key with placeholder substitution: `{0}`, `{1}`, … */
    $format: (key: K, ...args: (string | number)[]) => string;
};

function resolveModuleLocale(modName: string, locale: string): string {
    const mod = (I18NModuleDict as Record<string, Record<string, Record<string, string>>>)[modName];
    if (!mod) return locale;
    if (mod[locale]) return locale;
    // try fallback
    const fb = LOCALE_FALLBACK[locale];
    if (fb && mod[fb]) return fb;
    // try base language
    const base = locale.split('-')[0];
    for (const k of Object.keys(mod)) {
        if (k === base || k.startsWith(base + '-')) return k;
    }
    // default to first available
    const keys = Object.keys(mod);
    return keys.length > 0 ? keys[0] : locale;
}

function createModuleProxy<K extends string>(modName: string): ModuleProxy<K> {
    return new Proxy({} as ModuleProxy<K>, {
        get(_, key: string | symbol) {
            if (typeof key !== 'string') return undefined;

            // $format helper: I18N.note.$format('pagesOf', 1, 5)
            if (key === '$format') {
                return (formatKey: string, ...args: (string | number)[]) => {
                    const resolvedLocale = resolveModuleLocale(modName, currentLocale);
                    const dict = (I18NModuleDict as Record<string, Record<string, Record<string, string>>>)[modName]?.[
                        resolvedLocale
                    ];
                    const raw: string = dict?.[formatKey] ?? formatKey;
                    return args.reduce<string>((str, arg, i) => str.replaceAll(`{${i}}`, String(arg)), raw);
                };
            }

            const resolvedLocale = resolveModuleLocale(modName, currentLocale);
            const dict = (I18NModuleDict as Record<string, Record<string, Record<string, string>>>)[modName]?.[
                resolvedLocale
            ];
            if (dict && key in dict) return dict[key]; // primitive string
            return key; // fallback: return key name as primitive string
        },
    });
}

// ── main proxy ─────────────────────────────────────────────────────

// Module names that should be intercepted
const MODULE_NAMES = new Set(Object.keys(I18NModuleDict ?? {}));

// Cache module proxies so we don't recreate them on every access
const moduleProxyCache: Record<string, ModuleProxy<string>> = {};

type I18NType = {
    currentLocale: Locale;
    setLocale: (locale: Locale) => void;
} & {
    [M in keyof ModuleKeysMap]: ModuleProxy<ModuleKeysMap[M]>;
};

const i18nHandler: ProxyHandler<I18NType> = {
    get(_: I18NType, prop: string | symbol) {
        if (prop === 'currentLocale') return currentLocale;
        if (prop === 'setLocale') return setLocale;

        if (typeof prop === 'string') {
            // Module access: I18N.note.xxx
            if (MODULE_NAMES.has(prop)) {
                if (!moduleProxyCache[prop]) {
                    moduleProxyCache[prop] = createModuleProxy(prop);
                }
                return moduleProxyCache[prop];
            }
        }
        return undefined;
    },
};

export default new Proxy<I18NType>({} as I18NType, i18nHandler);
